"use client"

import type { MouseEvent } from "react"

import { usePathname } from "@/i18n/navigation"

/**
 * Click handler for category quick-nav links (`/menu#category-<id>`).
 *
 * When already on `/menu`, a soft-nav to a same-path hash appends to the current
 * fragment instead of replacing it, producing `…#category-a#category-b` on the
 * second click. We take over the hash change ourselves: `history.pushState` with
 * a bare `#fragment` resolves against the current URL and cleanly *replaces* the
 * fragment, then we smooth-scroll to the section (matching ScrollRestoration's
 * header-offset math). Off `/menu` we do nothing and let the Link navigate.
 *
 * Modifier/non-primary clicks are left alone so open-in-new-tab still works.
 */
export function useCategoryHashNav() {
  const pathname = usePathname()

  return (categoryId: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (pathname !== "/menu") return
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    )
      return

    e.preventDefault()
    const id = `category-${categoryId}`
    history.pushState(null, "", `#${id}`)

    const el = document.getElementById(id)
    if (!el) return
    const header = document.querySelector("header")
    const offset = header ? header.getBoundingClientRect().height + 16 : 0
    const top = el.getBoundingClientRect().top + window.scrollY - offset
    window.scrollTo({ top, behavior: "smooth" })
  }
}
